import { Injectable } from "@angular/core";
import moment from "moment";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, map, shareReplay, switchMap, tap } from "rxjs/operators";
import { of } from "rxjs";
import uniq from "lodash/uniq";
import { ICreateBooking, ISessionDate } from "../../bookings.service";
import {
  IBooking,
  IBookingAudioInput,
  IBookingRecurrence,
  ICaption,
  ICaptionSource,
  ICaptionValue,
  IGooglePaymentPayload,
  IOrganization,
  IPayment,
  IRecurrenceCalculate,
  IServiceTypesList,
  TBookingRecurrence,
  TBookingType,
  TPaymentType,
  TRecurrencePattern,
  TRecurrenceRange,
  TServiceTypeName,
  WeekDayFlags,
} from "../../../types";
import { CreateBookingProviderModule } from "./create-booking-provider.module";
import { AuthService } from "../../../core/auth.service";
import { Stripe } from "@stripe/stripe-js";
import { environment } from "../../../../environments/environment";
import {
  DIRECT_MICROPHONE_AUDIO_SOURCE_NAME,
  LEXI_ACCESS_SRV_ID,
  isAiLiveLexiMeetingBotAudioSelectionEnabled,
  isAiLiveLexiSelectableAudioSourceName,
  isLexiAccessServiceTypeName,
} from "../../../constants/ai-live";
import {
  TOPIC_MODEL_CURATION_SRV_ID,
  isTopicModelCurationServiceTypeName,
} from "../../../constants/topic-model-curation";
import { convertScheduleToDates, normalizeEpochToSeconds, normalizeScheduleTimeString } from "../../utils";
import { Guid } from "guid-typescript";
import { CapabilitiesService } from "../../capabilities.service";
import { IBookingDefaults, IUser, SettingsService } from "../../settings/settings.service";
import { AudioSourcesService } from "../../audio-sources.service";
import { DisplayCaptionsService } from "../../display-captions.service";
import { ICustomCaptionValue } from "../../shared/captions/captions.service";
import { CommonService, IMaintenance } from "../../common.service";
import { ConfirmationService, IConfirmation } from "../../confirmation.service";
import { weekend_days } from "src/app/constants";

const FIXED_FEE_PRODUCT_SKU = "live-captions-surcharge";

export const STEPS = [
  "Add general information",
  "Set time schedule",
  "Set frequency",
  "Add booking details",
  "Set audio source",
  "Set display captions",
  "VRI information",
  "Google payment method",
  "Confirm your booking",
  "Review your booking",
];

@Injectable({
  providedIn: CreateBookingProviderModule,
})
export class StepService {
  public clone_data!: any;
  public cap_values: ICustomCaptionValue[] = [];
  public timeZone?: string;
  public defaultTimeZone: string = this.auth.getUserTimeZone();
  public timeZoneTouched = false;
  public bookingType: TBookingType = "education";
  public serviceTypes: TServiceTypeName = "Remote Captioning";
  public title = "";
  public shortTitle = "";
  public students: string[] = [];
  public viewers: string[] = [];
  public selectedServiceType: TServiceTypeName = "Remote Captioning";
  public subServices = "";
  public point_of_contact =
    String(this.auth.getUserInfo().booking_defaults?.point_of_contact) ||
    String(
      `${this.auth.getUserInfo().first_name} ${this.auth.getUserInfo().last_name} - ${this.auth.getUserInfo().email}`,
    );
  public sign_language_type = "";
  public language_mode = "";
  public topic_model_language = "";
  public topic_model_language_other = "";
  public topic_model_update_existing = 0;
  public topic_model_curation_notes = "";
  // istanbul ignore next -- unreachable: `1 || 0` short-circuits on a literal truthy left operand, so `0` is dead.
  public open_access: number = 1 || 0;
  // istanbul ignore next -- unreachable: see open_access above; the `0` fallback can never be evaluated.
  public machine_translation: number = 1 || 0;
  public cloneID: number | undefined;
  public person_using_vri = "";
  public presenting_participant = "";
  public num_interpreters_needed = "";
  public preferred_interpreters = "";
  public interpreter_attire = "";
  public event_recorded_live_broadcast = "";
  public readyForNextStep$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isReadyForNextStep$: Observable<boolean> = this.readyForNextStep$$.asObservable().pipe(distinctUntilChanged());
  public cloneBooking: IBooking | undefined;
  public recurrenceType: TBookingRecurrence = "one-day booking";
  public startTime: string | undefined;
  public endTime: string | undefined;
  public startDate?: string;
  public hasValidResult = true;
  public recurrencePattern: TRecurrencePattern | undefined;
  public recurrenceRange: TRecurrenceRange | undefined;
  public recurrenceEndDate?: string;
  public exclusions?: string[] = [];
  public exclude_weekends: boolean = true;
  public excludeWeekend$$: Subject<boolean> = new Subject<boolean>();
  public showList$$: Subject<boolean> = new Subject<boolean>();
  public updateList$$: Subject<boolean> = new Subject<boolean>();
  public consumers?: number[];
  public booking_users?: string[] = [];
  public booking_created_for?: number;
  public org_id!: number;
  public billing_entity_id?: number;
  public prepMaterials: File[] = [];
  public audio?: IBookingAudioInput; // user defaults
  public captions?: ICaptionValue[]; // user defaults
  public notes = ""; // user defaults
  public allow_transcript; // user defaults
  public private_link_type: string | undefined = "anyone"; // user defaults
  public hide_consumer_dropdown?: string;
  public hide_private_viewer_dropdown?: string;
  public billing_reference_override?: string;
  public booking_billing_reference?: string;
  public billing_contact_email?: string;
  public payment_message?: string;
  public audio_source_checkbox_checked = true;
  public caption_source_checkbox_checked = true;
  public paymentType?: TPaymentType;
  public stripe?: Stripe;
  public stripeCard?: any;
  public this_org: number | undefined;
  public orgName: string | undefined;
  public hostDomains: string | undefined;
  public allowTranscript = false;
  private recalculateSessions$$: Subject<any> = new Subject();
  public recalculateSessions$: Observable<any> = this.recalculateSessions$$.asObservable();
  // Bounds the weekend-exclusion convergence retry in reconcileRecurrencePreview() — each
  // excluded date is backfilled by the backend with a replacement date that can itself land
  // on a weekend, so exclusion detection may need more than one round trip to fully settle.
  private static readonly MAX_EXCLUSION_RECONCILE_ATTEMPTS = 8;
  private exclusionReconcileAttempts = 0;
  // True while a convergence retry is pending (set synchronously inside reconcileRecurrencePreview,
  // before recurrencePreview$ delivers that emission downstream) — lets subscribers hold off
  // advancing the wizard until exclusions have actually settled, instead of racing the retries.
  public isReconcilingExclusions = false;
  // Single reconciled recurrence preview shared by the counter, calendar, and
  // recurrence step — replaces three independent, differently-timed
  // calcSessionRecurrences round-trips that could disagree with each other (LBP-230).
  public recurrencePreview$: Observable<ISessionDate[]> = this.recalculateSessions$$.asObservable().pipe(
    debounceTime(500),
    map(() => this.getRecurrenceInfo()),
    switchMap((info) => this.common.calcSessionRecurrences$(info)),
    map(({ new_session_dates }) => new_session_dates),
    tap((dates) => this.reconcileRecurrencePreview(dates)),
    shareReplay(1),
  );
  private currentStep = 1;
  private stepChanged$$: BehaviorSubject<number> = new BehaviorSubject<number>(this.currentStep);
  public stepChanged$: Observable<number> = this.stepChanged$$.asObservable();
  public shareable_caption_link = 0;
  public google_payment_method: IGooglePaymentPayload[] = [];
  public google_docs_transcript_link: string = "";
  public google_viewers: number = 0;
  public estimated_viewer_count?: number;

  public listOfUsers: IUser[] = [];

  private instantLexi: any = {};

  public audio_options = [];
  private audio_options_unfiltered: any[] = []; // Store unfiltered audio options for Ai-Live LEXI lookup
  private audio_options_global: any[] = []; // LBP-330: full (non-org-scoped) audio list, for Direct Mic fallback
  public caption_options = [];
  public selected_languages: any = [];
  public show_selected_languages = false;

  // cloning defaults
  public clone_audio?: IBookingAudioInput;
  public audio_has_params = false;

  public service_type_list: any = [];
  public enable_translation = false;
  public restrict_language = false;

  // Maintenance data storage
  public maintenanceData: IMaintenance[] = [];
  public maintenanceLoaded = false;
  public maintenanceError: string | null = null;

  constructor(
    private auth: AuthService,
    private capabilities: CapabilitiesService,
    private setting: SettingsService,
    private audioSources: AudioSourcesService,
    private display: DisplayCaptionsService,
    private common: CommonService,
    private confirmation: ConfirmationService,
  ) {
    const billing_entity = this.capabilities.current_be || this.auth.cached_billing_entity || {};
    const be_srv_types = this.auth.cached_billing_entity?.service_types || [];
    const all_srv_types = this.auth?.getServiceTypesList() || [{ name: "Remote Captioning" }];
    const default_srv_type = be_srv_types[0]
      ? all_srv_types?.find((i: any) => i.id === be_srv_types[0])?.name
      : "Remote Captioning";
    const serv_type_list = all_srv_types?.filter((st: any) => be_srv_types.includes(st.id));

    const serv_type_list_name = serv_type_list?.map((st: any) => st.name);

    this.instantLexi = serv_type_list?.find((s: any) => s.srv_id === "il");
    this.selectedServiceType = (
      serv_type_list_name.includes(billing_entity?.default_service_type || "")
        ? billing_entity?.default_service_type
        : default_srv_type
    ) as TServiceTypeName;

    let book_def = this.auth.getBookingDefaults();

    const { notes, private_link_type, allow_transcript, caption, audio, audio_caption_details, point_of_contact } =
      book_def;

    // set user defaults here
    this.notes = notes;
    this.private_link_type = private_link_type;
    this.allow_transcript = this.shouldHideTranscriptAndShareCaption() ? 1 : allow_transcript;
    this.point_of_contact = point_of_contact;
    this.audio_source_checkbox_checked = !!audio_caption_details;
    this.caption_source_checkbox_checked = !!audio_caption_details;

    // Load maintenance data
    this.loadMaintenanceData();

    // @ts-ignore
    this.capabilities.currentOrgId$$
      .pipe(
        tap((this_org) => {
          this.this_org = this_org;
        }),
      )
      .subscribe();
    // LBP-330: full (non-org-scoped) audio list, for getDirectMicrophoneInputAudio()'s fallback tier
    this.audioSources.audio$.subscribe((sources) => {
      this.audio_options_global = [...sources];
    });

    this.capabilities.currentOrgId$.subscribe((org_id) => {
      this.audioSources.getAudioByOrg$(org_id).subscribe((sources) => {
        let audio_options: any = [];
        sources.forEach((i: any) => {
          // @ts-ignore
          const { audio_input_id, audio_name, audio_params, organisations } = i;
          if (organisations.includes(org_id)) {
            audio_options = [
              ...audio_options,
              {
                audio_input_id,
                audio_name,
                audio_params,
              },
            ];
          }
        });

        // Store unfiltered audio options for Ai-Live LEXI lookup when service type changes
        this.audio_options_unfiltered = [...audio_options];

        // Filter out "Ai-Live LEXI" audio source when service type is not Ai-Live Lexi/Ai-Live LEXI
        // Note: "Ai-Live" (without LEXI) should remain available for other service types like Remote Captioning
        if (!this.isAiLexiLiveServiceType()) {
          audio_options = audio_options.filter((source: any) => {
            const name = source.audio_name || "";
            // Only filter out "Ai-Live LEXI" specifically, not "Ai-Live"
            return name !== "Ai-Live LEXI";
          });
        }

        if (audio) {
          const found = audio_options.find((aud: any) => aud.audio_input_id === audio);
          if (found) {
            this.audio = found as unknown as IBookingAudioInput;
          } else {
            this.audio = audio_options[0] as unknown as IBookingAudioInput;
          }
        } else {
          this.audio = audio_options[0] as unknown as IBookingAudioInput;
        }

        this.audio_options = audio_options;
      });

      this.display.captions$.subscribe((captions: ICaptionSource[]) => {
        let caption_options: any = [];
        let for_options: any = [];
        captions.forEach((i: any) => {
          // @ts-ignore
          const { caption_output_id, caption_output_name, caption_params, organisations } = i;
          if (organisations.includes(org_id)) {
            const payload = {
              caption_output_id,
              caption_output_name,
              caption_params: caption_params.map((__: any) => {
                const { caption_param_id } = __;
                return { caption_param_id, value: "" };
              }),
              destination_languages: "",
              machine_translation: undefined,
            };
            caption_options = [...caption_options, payload];

            for_options = [
              ...for_options,
              {
                ...payload,
                caption_output_name,
              },
            ];
          }
        });

        if (caption) {
          const found = caption_options.find((cap: any) => cap.caption_output_id === caption);
          if (found) {
            this.captions = [found];
          } else {
            this.captions = [caption_options[0]];
          }
        } else {
          this.captions = [caption_options[0]];
        }

        this.caption_options = for_options;
      });
      this.setting.getOrganizations$(org_id).subscribe((organisations) => {
        const org = organisations?.find((i) => i.org_id === org_id) || organisations[0];
        this.orgName = org.org_name;
        this.hostDomains = org.hd;
        this.allowTranscript = org.allow_transcripts;
        this.restrict_language = org.restrict_language;
      });
    });
  }

  public getServiceTypesList = () => {
    return this.auth
      .getServiceTypesList()
      .filter((st: any) => this.auth.cached_billing_entity?.service_types.includes(st.id));
  };

  private loadMaintenanceData(): void {
    this.maintenanceLoaded = false;
    this.maintenanceError = null;

    this.common.getMaintenance$().subscribe({
      next: (data: IMaintenance[]) => {
        this.maintenanceData = data;
        this.maintenanceLoaded = true;
      },
      error: (error: any) => {
        this.maintenanceError = "Failed to load maintenance data";
        this.maintenanceLoaded = true;
        console.error("Error loading maintenance data:", error);
      },
    });
  }

  public refreshMaintenanceData(): void {
    this.loadMaintenanceData();
  }

  /**
   * Check for maintenance conflicts and show dialog if needed
   */
  public checkAndHandleMaintenanceConflict = (): Observable<boolean> => {
    return this.common.checkAndHandleMaintenanceConflict$(
      this.startDate || "",
      this.startTime || "",
      this.endTime || "",
      this.getTimezone(),
      this.selectedServiceType,
      this.captions || [],
      this.billing_entity_id,
    );
  };

  /**
   * Check for overlapping bookings with the same meeting URL and show dialog if needed
   */
  public checkAndHandleMeetingUrlOverlap = (): Observable<boolean> => {
    return this.common.checkAndHandleMeetingUrlOverlap$(
      this.selectedServiceType,
      this.audio,
      this.startDate || "",
      this.startTime || "",
      this.endTime || "",
      this.getTimezone(),
      this.billing_entity_id,
    );
  };

  public isOnsiteCaptioning = () => {
    const service_types = this.getServiceTypesList();

    if (service_types?.length) {
      const found = service_types?.find((st: IServiceTypesList) => st.name === this.selectedServiceType);
      if (found?.srv_id === "oc") {
        return true;
      }
    }

    return false;
  };

  private _weekdays?: number;

  get weekdays() {
    return this._weekdays;
  }

  set weekdays(bitday: any) {
    if (bitday) {
      this._weekdays = parseInt(bitday);
    }
  }

  private _dayInterval = 1;

  get dayInterval() {
    return this._dayInterval;
  }

  set dayInterval(dayInterval: any) {
    this._dayInterval = parseInt(dayInterval);
  }

  private _weekInterval = 1;

  get weekInterval() {
    return this._weekInterval;
  }

  set weekInterval(weekInterval: any) {
    this._weekInterval = parseInt(weekInterval);
  }

  private _months?: number;

  get months() {
    return this._months;
  }

  set months(month: any) {
    if (month) {
      this._months = parseInt(month);
    }
  }

  private _monthDay?: number;

  get monthDay() {
    return this._monthDay;
  }

  set monthDay(day: any) {
    if (day) {
      this._monthDay = parseInt(day);
    }
  }

  private _weekNumber?: number;

  get weekNumber() {
    return this._weekNumber;
  }

  set weekNumber(week: any) {
    if (week) {
      this._weekNumber = parseInt(week);
    }
  }

  private _Z?: number;

  get Z() {
    return this._Z;
  }

  set Z(zvalue: any) {
    if (zvalue) {
      this._Z = parseInt(zvalue);
    }
  }

  result(): ICreateBooking {
    const user = this.auth.getUserInfo();
    const user_email = user.email;

    const [start_epoch, duration_mins] = this.getStartDateAndDuration();
    const booking_product_sku =
      this.bookingType === "education"
        ? "live-captions-complex?source_lang=english&audience=group&profile=low&destination=" /* + this.captions*/
        : "unknown";
    const newString: string = String(this.captions?.map((_) => _.destination_languages || ""));
    const audioPrimary =
      String(this.audio?.source_languages || "")
        .split(",")[0]
        ?.trim() || "";
    const defaultDestinationWhenEmpty =
      !audioPrimary || audioPrimary.toLowerCase() === "english" ? "English" : audioPrimary;
    // LBP-366: MT only applies when the first caption row targets a language other than the source
    const sourceLang = (audioPrimary || "English").toLowerCase();
    const hasTranslationTarget = String(this.captions?.[0]?.destination_languages || "")
      .split(",")
      .some((l) => !!l.trim() && l.trim().toLowerCase() !== sourceLang);
    const machine_trans: String = hasTranslationTarget
      ? String(this.captions?.filter((item, idx) => idx < 1).map((_) => _.machine_translation)) || "0"
      : "0";

    this.viewers.push(user_email);

    if (!this.point_of_contact) {
      // istanbul ignore next -- unreachable: the "First Last - email" template literal always contains
      // " - ", so it is never falsy and the trailing `|| ""` can never be evaluated.
      this.point_of_contact = String(
        user.booking_defaults?.point_of_contact || `${user.first_name} ${user.last_name} - ${user.email}` || "",
      );
    }

    // making sure all emails has only one record
    let allowedViewers: string | undefined = uniq(this.viewers).join(",");
    let attendees = uniq(this.students).join(",");

    if (this.private_link_type === "anyone") {
      const trimmedHostDomains = this.hostDomains?.trim();
      allowedViewers = trimmedHostDomains || allowedViewers;
    }

    let result: ICreateBooking = {
      attendees,
      start_epoch,
      title: this.title,
      short_title: this.shortTitle,
      billing_reference_override: this.billing_reference_override,
      billing_entity_id: this.billing_entity_id as number,
      // @ts-ignore
      billing_contact_email: "" || this.billing_contact_email,
      captioner_notes: this.notes || "",
      booking_product_sku,
      fixed_fee_product_sku: FIXED_FEE_PRODUCT_SKU,
      tags: this.bookingType,
      duration_mins,
      destination_language: newString || defaultDestinationWhenEmpty,
      source_language: this.audio?.source_languages || "English",
      selectedServiceType: this.selectedServiceType,
      subServices: this.subServices,
      sign_language_type: String(this.sign_language_type),
      language_mode: String(this.language_mode),
      open_access: this.open_access,
      topic_model_language: this.topic_model_language,
      topic_model_language_other: this.topic_model_language_other,
      topic_model_update_existing: this.topic_model_update_existing,
      topic_model_curation_notes: this.topic_model_curation_notes,
      person_using_vri: String(this.person_using_vri),
      presenting_participant: String(this.presenting_participant),
      num_interpreters_needed: String(this.num_interpreters_needed),
      preferred_interpreters: String(this.preferred_interpreters),
      interpreter_attire: String(this.interpreter_attire),
      event_recorded_live_broadcast: String(this.event_recorded_live_broadcast),
      allowed_viewer: allowedViewers,
      viewerGuid: String(Guid.create()).replace(/[\[\]-]+/g, ""),
      allow_transcript: this.allow_transcript,
      machine_translation: Number(machine_trans),
      booked_for_user: Number(this.booking_created_for),
      point_of_contact: String(this.point_of_contact),
      shareable_caption_link: 0,
      google_payment_method: [],
      google_viewers: 0,
      google_docs_transcript_link: this.google_docs_transcript_link,
      audio_input: this.audio,
      caption_outputs: this.captions,
      consumers: this.consumers,
      estimated_viewer_count: this.estimated_viewer_count,
    };

    if (this.timeZone !== this.defaultTimeZone) {
      result.time_zone_override = this.timeZone;
    }

    const recurrence = this.getRecurrence();
    if (recurrence) {
      Object.assign(result, { recurrence });
    }

    if (this.isGoogleServiceType()) {
      Object.assign(result, {
        shareable_caption_link: this.shareable_caption_link,
        google_payment_method: this.google_payment_method,
        google_viewers: this.google_viewers,
      });
    }

    if (this.isLiveStreamingLexi()) {
      const audio_found: any = this.audio_options.find((aud: any) => aud.audio_name === "RTMP");
      const caption_found = this.caption_options.find((cap: any) => cap.caption_output_name === "RTMP");

      if (caption_found && audio_found) {
        result = {
          ...result,
          audio_input: {
            ...this.audio,
            audio_input_id: audio_found?.audio_input_id as number,
            audio_name: audio_found?.audio_name as string,
          } as IBookingAudioInput,
          caption_outputs: [caption_found],
        };
      }
    }

    // Handle Ai-Live LEXI audio/caption setting based on feature flag
    // LBP-330: Ai-Live LEXI always forces the "Ai-Live LEXI" caption. Audio is only forced to
    // Direct Microphone Input when meeting-bot audio selection is unavailable (flag/region gate
    // off); when it's enabled, the user's own audio-step selection (this.audio) is honored as-is
    // so their Zoom/Meet/Teams pick isn't silently discarded.
    if (this.isAiLexiLiveServiceType()) {
      const caption_found = this.caption_options.find((cap: any) => cap.caption_output_name === "Ai-Live LEXI");

      if (caption_found) {
        result = {
          ...result,
          caption_outputs: [caption_found],
        };

        if (!this.isAiLiveLexiMeetingBotAudioSelectionEnabled()) {
          const audio_found = this.getDirectMicrophoneInputAudio();
          result = {
            ...result,
            audio_input: {
              ...this.audio,
              audio_input_id: audio_found?.audio_input_id as number,
              audio_name: audio_found?.audio_name as string,
              audio_params: audio_found?.audio_params,
            } as IBookingAudioInput,
          };
        }
      }
    }

    // LBP-330: LEXI Access always uses "Ai-Live LEXI" caption; audio is the user's choice
    // among the 3 meeting-platform sources (filtered in audio.component.ts).
    if (this.isLexiAccessServiceType()) {
      const caption_found = this.caption_options.find((cap: any) => cap.caption_output_name === "Ai-Live LEXI");

      if (caption_found) {
        result = {
          ...result,
          caption_outputs: [caption_found],
        };
      }
    }

    if (this.restrict_language) {
      result = {
        ...result,
        destination_language: this.audio?.source_languages,
        caption_outputs: result?.caption_outputs?.map((i) => ({
          ...i,
          destination_languages: this.audio?.source_languages,
        })),
      };
    }

    // For VRI bookings, clear destination languages on all caption outputs
    if (this.selectedServiceType === "Video Remote Interpreting (VRI)") {
      result = {
        ...result,
        source_language: "English",
        destination_language: "English",
        caption_outputs:
          result?.caption_outputs?.map((i) => ({
            ...i,
            destination_languages: "English",
          })) || [],
      };
    }

    result = this.transFormAudioCaption(result);

    return result;
  }

  // this transformer is created because it is super hard to pinpoint where in the code the audio is being dealt with...
  transFormAudioCaption(booking: any) {
    const { audio_input, caption_outputs } = booking;
    // Try to find audio_name from filtered options first, then from unfiltered options
    // This is needed because "Ai-Live LEXI" may be filtered out from audio_options
    const audioOption =
      this.audio_options?.find((a: any) => a.audio_input_id === audio_input?.audio_input_id) ||
      this.audio_options_unfiltered?.find((a: any) => a.audio_input_id === audio_input?.audio_input_id);
    return {
      ...booking,
      audio_input: {
        ...audio_input,
        audio_name: audioOption?.audio_name || audio_input?.audio_name,
      },
      caption_outputs: caption_outputs?.map((cap_out: any) => ({
        ...cap_out,
        caption_output_name: (
          this.caption_options?.find((c: any) => c.caption_output_id === cap_out.caption_output_id) || ({} as any)
        )?.caption_output_name,
      })),
    };
  }
  public isGoogleServiceType() {
    return ["Google Event - Remote Captioning", "Google Event - Video Relay Interpreting"].includes(
      this.selectedServiceType,
    );
  }

  public isGoogleEventVRIServiceType() {
    return this.selectedServiceType === "Google Event - Video Relay Interpreting";
  }

  public isRTIServiceType() {
    return this.selectedServiceType === "Remote Text Interpreting (RTI)";
  }

  public isVRIServiceType() {
    return this.selectedServiceType === "Video Remote Interpreting (VRI)";
  }

  public isAiLexiLiveServiceType() {
    const serviceTypeLower = (this.selectedServiceType || "").toLowerCase();
    return ["ai-live lexi"].includes(serviceTypeLower);
  }

  /** LBP-330: identity is srv_id "la", not display name (mirrors isOnsiteCaptioning()'s srv_id lookup). */
  public isLexiAccessServiceType = (): boolean => {
    const service_types = this.getServiceTypesList();
    if (service_types?.length) {
      const found = service_types?.find((st: IServiceTypesList) => st.name === this.selectedServiceType);
      if (found) {
        return found.srv_id === LEXI_ACCESS_SRV_ID;
      }
    }
    return isLexiAccessServiceTypeName(this.selectedServiceType);
  };

  /** LBP-322: identity is srv_id "tmc" (mirrors isLexiAccessServiceType()'s srv_id lookup). */
  public isTopicModelCurationServiceType = (): boolean => {
    const service_types = this.getServiceTypesList();
    const found = service_types?.find((st: IServiceTypesList) => st.name === this.selectedServiceType);
    return found ? found.srv_id === TOPIC_MODEL_CURATION_SRV_ID : isTopicModelCurationServiceTypeName(this.selectedServiceType);
  };

  /** LBP-330: both Ai-Live LEXI and LEXI Access always force the "Ai-Live LEXI" caption. */
  public forcesAiLiveLexiCaption(): boolean {
    return this.isAiLexiLiveServiceType() || this.isLexiAccessServiceType();
  }

  /** LBP-330: LEXI Access reuses the existing meeting-bot region gate (US/UK/AU + feature flag). */
  public isLexiAccessAvailable(): boolean {
    return this.isAiLiveLexiMeetingBotAudioSelectionEnabled();
  }

  /** Env flag plus billing-entity SAM region (US / AMERICAS only for meeting-bot audio). */
  public isAiLiveLexiMeetingBotAudioSelectionEnabled(): boolean {
    const be = this.capabilities.current_be || this.auth.cached_billing_entity;
    return isAiLiveLexiMeetingBotAudioSelectionEnabled({
      regionId: be?.region_id,
      regions: this.auth.regions,
    });
  }

  /**
   * Get "Ai-Live LEXI" audio source from unfiltered audio options
   * This is needed because audio_options may have "Ai-Live LEXI" filtered out
   * when the service type was not "Ai-Live Lexi" at the time of loading
   */
  public getAiLiveLexiAudio(): any {
    return this.audio_options_unfiltered.find((aud: any) => aud.audio_name === "Ai-Live LEXI");
  }

  /**
   * LBP-330: Ai-Live LEXI's forced audio source. Direct Microphone Input may not be assigned to
   * every org, so fall back org list → global list → the legacy "Ai-Live LEXI" audio row (matches
   * today's pre-split forced-audio identity, for data setups that never had a Direct Mic row).
   */
  public getDirectMicrophoneInputAudio(): any {
    return (
      this.audio_options_unfiltered.find((aud: any) => aud.audio_name === DIRECT_MICROPHONE_AUDIO_SOURCE_NAME) ||
      this.audio_options_global.find((aud: any) => aud.audio_name === DIRECT_MICROPHONE_AUDIO_SOURCE_NAME) ||
      this.getAiLiveLexiAudio()
    );
  }

  public isAiLexiLiveWithIntegratedAudio() {
    if (!this.isAiLexiLiveServiceType()) {
      return false;
    }
    console.log("this.audio", this.audio);

    return this.audio && isAiLiveLexiSelectableAudioSourceName(this.audio.audio_name);
  }

  public isNotetakingServiceType() {
    return this.selectedServiceType === "Notetaking";
  }

  public isLiveStreamingLexi() {
    return this.selectedServiceType === "Live Streaming - LEXI";
  }

  public isInstantLexi() {
    const serviceTypeLower = (this.selectedServiceType || "").toLowerCase();
    return ["instant lexi"].includes(serviceTypeLower);
  }

  /** LBP-323: LEXI service types with no human captioner — speaker names and prep materials aren't applicable. */
  public isLexiServiceTypeWithoutSpeakers(): boolean {
    return (
      this.isAiLexiLiveServiceType() ||
      this.isLexiAccessServiceType() ||
      this.isLiveStreamingLexi() ||
      this.isInstantLexi()
    );
  }

  public isRemoteCaptioning() {
    return this.selectedServiceType === "Remote Captioning";
  }

  public isRemoteCaptioningWithGoogle() {
    return this.isRemoteCaptioning() || this.isGoogleRemoteCaptioning();
  }

  public isGoogleRemoteCaptioning() {
    return this.selectedServiceType === "Google Event - Remote Captioning";
  }

  public shouldShowEstimatedViewerCount() {
    const serviceTypeLower = (this.selectedServiceType || "").toLowerCase();
    const serviceTypes = ["remote captioning", "google event - remote captioning", "instant lexi"];
    const isAiLiveLexi = serviceTypeLower === "ai-live lexi" || serviceTypeLower.includes("ai-live");

    return serviceTypes.includes(serviceTypeLower) || isAiLiveLexi || this.isLexiAccessServiceType();
  }

  isOneDayBooking = () => {
    return this.recurrenceType === "one-day booking";
  };

  public shouldHideTranscriptAndShareCaption() {
    return ["Google - Notetaking"].includes(this.capabilities.current_be?.name);
  }

  // LBP-368: outputs confirmed to carry no shareable Ai-Live caption link.
  private static readonly CAPTION_OUTPUT_NAMES_WITHOUT_LINK = [
    "RTMP", "iCap", "IP Encoder", "YouTube", "Other",
    "AV Connection", "Monitor", "Projector",
  ];

  public hasCaptionOutputWithLink(): boolean {
    return (this.captions || []).some((c: any) => {
      const name = (c?.caption_output_name || "").trim().toLowerCase();
      return !StepService.CAPTION_OUTPUT_NAMES_WITHOUT_LINK.some((x) => x.toLowerCase() === name);
    });
  }

  next() {
    // there should be a better way of handling this other than multiple if statements

    if (this.isNotetakingServiceType() || this.isGoogleEventVRIServiceType()) {
      if (this.currentStep === 5) {
        this.currentStep = this.currentStep + 1;
      }
    }

    // if (this.isLiveStreamingLexi()) {
    //     if (this.currentStep === 4) {
    //         this.currentStep = this.currentStep + 2;
    //     }
    // }

    if (this.currentStep === 2 && this.isOneDayBooking()) {
      this.currentStep++;
    }

    // Topic Model Curation has no "Add booking details"/audio-source/display-captions steps
    // (raw 4,5,6) — landing exactly on raw step 6 here lets the existing non-VRI captions-skip
    // check below, and the existing TMC exclusion in the raw-step-7 check further down, cascade
    // naturally onto raw step 8.
    if (this.currentStep === 3 && this.isTopicModelCurationServiceType()) {
      this.currentStep += 3;
    }

    if (this.currentStep === 6 && !this.isVRIServiceType()) {
      this.currentStep++;
    }

    if (this.currentStep === 5 && this.isVRIServiceType()) {
      this.currentStep++;
    }

    // istanbul ignore if -- unreachable: the `currentStep === 6 && !isVRIServiceType()` block above already
    // advances every non-VRI booking off step 6, so still being on step 6 here requires a booking that is
    // both VRI and a Google service type, which no single selectedServiceType value can be.
    if (this.currentStep === 6 && this.isGoogleServiceType()) {
      this.currentStep++;
    }

    if (this.currentStep === 7 && !this.isGoogleServiceType() && !this.isTopicModelCurationServiceType()) {
      this.currentStep++;
    }

    if (this.currentStep === 5 && this.shouldHideTranscriptAndShareCaption()) {
      this.currentStep = this.currentStep + 3;
    }

    // LBP-330: skip the audio-source step for Ai-Live LEXI when meeting-bot audio selection is
    // unavailable — there's no user choice to make, audio is forced to Direct Microphone Input.
    if (this.currentStep === 4 && this.isAiLexiLiveServiceType() && !this.isAiLiveLexiMeetingBotAudioSelectionEnabled()) {
      this.currentStep++;
    }

    this.stepChanged$$.next(++this.currentStep);
    this.readyForNextStep$$.next(false);
  }

  previous() {
    // there should be a better way of handling this other than multiple if statements

    if (this.isNotetakingServiceType() || this.isGoogleEventVRIServiceType()) {
      if (this.currentStep === 9) {
        this.currentStep = this.currentStep - 3;
      }
    }

    // if (this.isLiveStreamingLexi()) {
    //     if (this.currentStep === 9) {
    //         this.currentStep = this.currentStep - 4;
    //     }
    // }

    // Mirror of the next() Topic Model Curation skip above — placed before the one-day-booking
    // check below so a one-day TMC booking's backward nav cascades the extra -1 for the now-skipped
    // frequency step, symmetric with next()'s ordering.
    if (this.isTopicModelCurationServiceType()) {
      if (this.currentStep === 8) {
        this.currentStep -= 4; // 8 -> 4 (skips 7 [pass-through], 6, 5)
      }
    }

    if (this.isOneDayBooking()) {
      if (this.currentStep === 4) {
        this.currentStep--;
      }
    }

    if (this.isVRIServiceType()) {
      if (this.currentStep === 7) {
        this.currentStep--;
      }
    }

    if (!this.isVRIServiceType() && this.isGoogleEventVRIServiceType()) {
      if (this.currentStep === 8) {
        this.currentStep--;
      }
    }

    if (this.isGoogleRemoteCaptioning()) {
      if (this.currentStep === 8) {
        this.currentStep--;
      }
    }

    if (!this.isGoogleServiceType() && !this.isGoogleEventVRIServiceType() && !this.isTopicModelCurationServiceType()) {
      if (this.currentStep === 9) {
        this.currentStep = this.currentStep - 2;
      }
    }

    if (this.isGoogleEventVRIServiceType()) {
      // istanbul ignore if -- unreachable: the earlier `!isVRIServiceType() && isGoogleEventVRIServiceType()`
      // block already steps a Google Event VRI booking off step 8, so step 8 can never still be current here.
      if (this.currentStep === 8) {
        this.currentStep = this.currentStep - 2;
      }
    }

    if (this.currentStep === 7 && this.shouldHideTranscriptAndShareCaption()) {
      this.currentStep = this.currentStep - 1;
    }

    // LBP-330: mirror of the next() skip above, walking back past the audio-source step for
    // Ai-Live LEXI when meeting-bot audio selection is unavailable.
    if (this.currentStep === 6 && this.isAiLexiLiveServiceType() && !this.isAiLiveLexiMeetingBotAudioSelectionEnabled()) {
      this.currentStep--;
    }

    this.stepChanged$$.next(--this.currentStep);
  }

  current(): number {
    return this.currentStep;
  }

  public getStartDateAndDuration(): [number, number] {
    const [start, end] = convertScheduleToDates(
      this.recurrenceType,
      this.startDate || "",
      normalizeScheduleTimeString(this.startTime),
      normalizeScheduleTimeString(this.endTime),
      undefined,
      this.getTimezone(),
    );

    const duration = Math.round((end.toSeconds() - start.toSeconds()) / 60);

    const base_d = moment(this.startDate + " " + "00:00");
    const today = moment(this.startDate + " " + this.startTime);
    const today_et = moment(this.startDate + " " + this.endTime);
    const m_st = moment.duration(moment(today).diff(moment(base_d))).asMinutes(); // moment start time
    const m_et = moment.duration(moment(today_et).diff(moment(base_d))).asMinutes(); // moment end time

    // if start time is greater than end time...it means the end time is on the other day
    if (m_st > m_et) {
      let tomorrow: any = moment(this.startDate).add(1, "day").format("YYYY-MM-DD");
      tomorrow = moment(tomorrow + " " + this.endTime);
      const moment_duration = moment.duration(moment(tomorrow).diff(moment(today)));
      const moment_minutes = moment_duration.asMinutes();
      return [normalizeEpochToSeconds(start.toSeconds()), moment_minutes];
    }

    return [normalizeEpochToSeconds(start.toSeconds()), duration];
  }

  getRecurrenceInfo(): IRecurrenceCalculate {
    const [start_epoch] = this.getStartDateAndDuration();
    return {
      start_epoch,
      time_zone: this.getTimezone(),
      recurrence: this.getRecurrence() as IBookingRecurrence,
    };
  }

  getRecurrence(bitday?: number): IBookingRecurrence | null {
    let result = null;
    if (this.recurrencePattern && this.recurrenceRange) {
      const recurrence: IBookingRecurrence = {
        pattern: this.recurrencePattern,
        range: this.recurrenceRange,
        exclusions: this.exclusions,
      };
      if (this.recurrenceRange === "end_after_Z_occurrences") {
        Object.assign(recurrence, { Z: this.Z });
      } else {
        Object.assign(recurrence, { end_date: this.recurrenceEndDate });
      }
      switch (this.recurrencePattern) {
        case "every_X_days":
          // daily
          Object.assign(recurrence, { X: this.dayInterval });
          break;
        case "every_X_weeks_on_bitdays":
          Object.assign(recurrence, { X: this.weekInterval, bitday: bitday || this.weekdays || 0 });
          // weekly
          break;
        case "on_day_X_every_Y_months":
          // monthly
          Object.assign(recurrence, { X: this.monthDay || 0, Y: this.months || 1 });
          break;
        case "on_X_bitday_every_Y_months":
          // monthly
          Object.assign(recurrence, {
            bitday: bitday || this.weekdays || WeekDayFlags.Monday,
            X: this.weekNumber || 1,
            Y: this.months || 1,
          });
          break;
      }
      result = recurrence;
    }
    return result;
  }

  getPaymentInfo(): IPayment {
    if (!this.paymentType) {
      throw new Error("Payment type was not set");
    }
    const type: TPaymentType = this.paymentType as TPaymentType;

    return Object.assign(
      {},
      { type },
      this.paymentType === "upfront"
        ? {
            stripe: this.stripe as Stripe,
            stripeCard: this.stripeCard,
          }
        : {},
    ) as IPayment;
  }

  public getTimezone(): string {
    return this.timeZone || (this.defaultTimeZone as string);
  }

  public getBillingEnt(): number {
    return <number>this.billing_entity_id;
  }

  public recalculateSessions(): void {
    // Reset the weekend-exclusion convergence budget on every genuine external trigger
    // (toggling the checkbox, editing an exception date, initial mount). Internal convergence
    // retries call recalculateSessions$$ directly, bypassing this method, so a still-converging
    // previous action can never silently steal retry budget from an unrelated later one.
    this.exclusionReconcileAttempts = 0;

    if (this.recurrenceType !== "one-day booking") {
      this.showList$$.next(false);
      this.updateList$$.next(false);

      setTimeout(() => {
        this.showList$$.next(true);
        this.updateList$$.next(true);
      }, 1000);
    }
    this.recalculateSessions$$.next(true);
  }

  // Final writer for common.recurrence_dates and the startDate correction — replaces two
  // previously-racing writers (this and RecurrenceComponent's own subscription). Note
  // CommonService.calcSessionRecurrences$'s own tap writes recurrence_dates first, one stage
  // earlier in the same recurrencePreview$ pipe, without the `excluded` flag; this method's
  // write always runs after it and is the one anything outside this pipe should rely on.
  // this.exclusions is recomputed here, after recurrence_dates is freshly written in
  // this same synchronous callback, so it is never one cycle stale (LBP-230).
  private reconcileRecurrencePreview(dates: ISessionDate[]): void {
    this.common.recurrence_dates = dates?.map(({ date, excluded }: any) => ({
      date,
      excluded,
      day: moment(date).format("dddd"),
    }));

    const new_dates = dates?.filter((d) => !d.excluded);

    // start_date may predate the first real occurrence (e.g. May 20 typed but
    // "first Monday" shifts the series to June 1). Keep start_epoch aligned.
    if (new_dates?.length) {
      this.startDate = new_dates[0].date;
    }

    const reconciled = this.calculateSessionExclusions();

    // A weekend date excluded on this round is backfilled by the backend with a replacement
    // date (to keep the requested count/date-range), and that replacement can itself land on
    // a weekend. Exclusions only ever grow while exclude_weekends is on, so a strict length
    // increase means this round surfaced a fresh weekend date the previous round couldn't have
    // seen — resubmit so the backend backfills against the fuller exclusion set (LBP-230 QA
    // follow-up: previously this ran once and only ever caught the first weekend).
    const foundNewWeekendDates = this.exclude_weekends && reconciled.length > (this.exclusions?.length ?? 0);

    this.exclusions = reconciled;

    if (foundNewWeekendDates && this.exclusionReconcileAttempts < StepService.MAX_EXCLUSION_RECONCILE_ATTEMPTS) {
      this.exclusionReconcileAttempts++;
      this.isReconcilingExclusions = true;
      this.recalculateSessions$$.next(true);
    } else {
      this.exclusionReconcileAttempts = 0;
      this.isReconcilingExclusions = false;
    }
  }

  calculateSessionExclusions() {
    const exclude_weekends = this.exclude_weekends;
    const exclusions = this.exclusions;
    const dates = this.common.recurrence_dates;
    const weekend_dates = dates?.filter((d) => weekend_days.includes(d.day)).map((d) => d.date);
    const non_weekend_dates = exclusions?.filter((d) => !weekend_days.includes(moment(d).format("dddd")));

    let new_exclusions: string[];

    if (exclude_weekends) {
      new_exclusions = uniq(weekend_dates);
      const new_dates = non_weekend_dates?.length ? [...non_weekend_dates, ...weekend_dates] : weekend_dates;
      new_exclusions = uniq(new_dates);
    } else {
      new_exclusions = uniq(<string[]>exclusions?.filter((d) => !weekend_dates.includes(d)));
    }

    return new_exclusions;
  }

  isEducation(): boolean {
    return this.bookingType === "education";
  }

  getCurrentStepNumber$(): Observable<number> {
    return this.stepChanged$.pipe(
      map((num) => {
        let new_num = num;
        if (this.recurrenceType === "one-day booking" && num > 2) {
          new_num = new_num - 1;
        }
        if (this.selectedServiceType !== "Video Remote Interpreting (VRI)" && num >= 7) {
          new_num = new_num - 1;
        }

        if (this.selectedServiceType === "Video Remote Interpreting (VRI)" && num >= 7) {
          new_num = new_num - 1;
        }

        if (this.isNotetakingServiceType()) {
          if (num >= 9) {
            new_num = new_num - 2;
          }
        }

        if (this.isGoogleEventVRIServiceType()) {
          if (num >= 8) {
            new_num = new_num - 1;
          }
        }

        // Topic Model Curation skips raw steps 4,5,6 entirely (see next()/previous()) — the
        // generic `num >= 7` non-VRI `-1` above already accounts for step 7's pass-through, so
        // this only needs to subtract the additional 3 skipped steps.
        if (this.isTopicModelCurationServiceType()) {
          if (num >= 8) {
            new_num = new_num - 3;
          }
        }

        // LBP-330: Ai-Live LEXI's progress display always collapses the audio-source and
        // confirmation steps out of the count, regardless of the meeting-bot audio flag — the
        // wizard's actual step-index navigation (see next()/previous()) still visits the audio
        // step when the flag is on, this only affects the "step N of total" display.
        if (this.isAiLexiLiveServiceType()) {
          if (num >= 5) {
            new_num = new_num - 1;
          }
          if (num >= 9) {
            new_num = new_num - 1;
          }
        }

        return new_num;
      }),
    );
  }

  getTotalDisplaySteps$(): Observable<number> {
    return this.stepChanged$.pipe(
      map(() => {
        const num = STEPS.length;
        let new_num = num;
        if (this.recurrenceType === "one-day booking" && num > 2) {
          new_num = new_num - 1;
        }
        if (this.selectedServiceType !== "Video Remote Interpreting (VRI)" && num >= 7) {
          new_num = new_num - 1;
        }
        if (this.selectedServiceType === "Video Remote Interpreting (VRI)" && num >= 7) {
          new_num = new_num - 1;
        }
        if (this.isNotetakingServiceType()) {
          if (num >= 9) {
            new_num = new_num - 2;
          }
        }
        if (this.isGoogleEventVRIServiceType()) {
          if (num >= 8) {
            new_num = new_num - 1;
          }
        }
        // See matching comment in getCurrentStepNumber$() above.
        if (this.isTopicModelCurationServiceType()) {
          if (num >= 8) {
            new_num = new_num - 3;
          }
        }
        // LBP-330: see matching comment in getCurrentStepNumber$() above.
        if (this.isAiLexiLiveServiceType()) {
          if (num >= 5) {
            new_num = new_num - 1;
          }
          if (num >= 9) {
            new_num = new_num - 1;
          }
        }
        return new_num;
      }),
      distinctUntilChanged(),
    );
  }

  getCurrentStepTitle$(): Observable<string> {
    return this.stepChanged$.pipe(
      map((step) => {
        let result = STEPS[step - 1];
        if (step === 3) {
          result = "Set frequency for " + this.recurrenceType + " booking";
        }
        if (step === 8 && this.isTopicModelCurationServiceType()) {
          result = "Topic model details";
        }
        return result;
      }),
    );
  }
}
